import { useEffect } from "react";
import { toast } from "sonner";
import { useTranslations } from 'use-intl';
import { useLatest } from '@/hooks/useLatest';
import { useFormatters } from "@financialharmony/ui";

/** The shape both callers' transaction lists share — all this hook actually reads. */
type HighlightableTransaction = { id: string; date: string; matched: boolean };

/**
 * Selects the tab holding the transaction named in `?transactionId=`, and tells the user
 * whether it was found.
 *
 * Extracted because Bank.tsx and Transactions.tsx carried this effect character for
 * character — the second copy of a block is where it gets extracted, and jscpd flags it as
 * a clone the moment either copy is touched. Both pages link here from elsewhere in the app
 * (a match confirmation, a bank-account detail row), so a stale `transactionId` that no
 * longer resolves is a real case, not a hypothetical: it has to say so rather than silently
 * leaving the user on a page that looks unchanged.
 */
export function useHighlightTransactionFromUrl(
  transactionIdFromUrl: string | null,
  transactions: HighlightableTransaction[],
  setSelectedTab: (tab: "matched" | "unmatched") => void,
): void {
  const t = useTranslations('consultantToasts');
  const tRef = useLatest(t);
  const formatDateRef = useLatest(useFormatters().formatDate);
  useEffect(() => {
    if (!transactionIdFromUrl || transactions.length === 0) return;

    const target = transactions.find((t) => t.id === transactionIdFromUrl);
    if (!target) {
      toast.error(tRef.current('highlight.notFoundTitle'), {
        description: tRef.current('highlight.notFoundBody'),
      });
      return;
    }

    setSelectedTab(target.matched ? "matched" : "unmatched");
    // Neutral, not a success: the user asked to look at something and it is now on screen.
    toast(tRef.current('highlight.foundTitle'), {
      description: tRef.current('highlight.foundBody', { date: formatDateRef.current(target.date) }),
    });
    // `toast` is a module import from sonner and `tRef` a ref, so neither is a dependency: a language
    // switch must not re-announce the transaction.
  }, [transactionIdFromUrl, transactions, setSelectedTab, tRef, formatDateRef]);
}
